/**
 * 🔄 Phase A2.2 — Helper centralisé pour le filtrage des employés
 * éligibles à être assignés à un Bon de Travail (BT).
 *
 * Règle (Eliot, 2026-05-04) :
 *   Les sous-traitants et installateurs ne peuvent PAS être assignés à un BT
 *   (interne ou externe). Ils sont également exclus du payout du pool bonus
 *   (cf. backend `routers/bonuses.py`).
 *
 * Ce helper vise les SÉLECTEURS d'employés dans les formulaires de
 * CRÉATION / ÉDITION d'un BT. Il NE touche PAS aux punches (les sous-traitants
 * peuvent toujours se puncher sur un BT existant — cf. `SectionPointage.jsx`),
 * et il est NON DESTRUCTIF pour les BT historiques (un sous-traitant déjà
 * dans `bon.employes_assignes` reste affiché ; on filtre seulement la liste
 * proposée à l'utilisateur).
 *
 * Robustesse :
 *   - Vérifie à la fois `role_terrain` ET `type_employe` (les 2 champs sont
 *     utilisés dans le code historique selon les modules → on couvre les 2).
 *   - Comparaison case-insensitive et tolérante au null/undefined.
 *   - Si on doit ajouter d'autres rôles à exclure plus tard (ex: "stagiaire"),
 *     un seul endroit à modifier : `ROLES_EXCLUS_BT`.
 *
 * @example
 *   import { filterEmployeesForBT } from '@/lib/btEmployees';
 *   const eligibles = filterEmployeesForBT(employees);
 *   // → DEMO_ST (sous-traitant) absent
 */

export const ROLES_EXCLUS_BT = ["sous_traitant", "installateur"];

/**
 * Retourne la sous-liste des employés assignables à un BT.
 * Les employés exclus sont ceux dont `role_terrain` OU `type_employe`
 * appartient à `ROLES_EXCLUS_BT` (case-insensitive).
 *
 * @param {Array<Object>|null|undefined} employees
 * @returns {Array<Object>} liste filtrée (jamais null)
 */
export function filterEmployeesForBT(employees) {
  if (!Array.isArray(employees)) return [];
  return employees.filter(e => {
    const rt = String(e?.role_terrain || "").toLowerCase().trim();
    const te = String(e?.type_employe || "").toLowerCase().trim();
    return !ROLES_EXCLUS_BT.includes(rt) && !ROLES_EXCLUS_BT.includes(te);
  });
}

/**
 * Indique si un employé donné est exclu du sélecteur BT.
 * Utile pour afficher un badge sur les employés legacy déjà assignés
 * (sans les retirer de `bon.employes_assignes`).
 *
 * @param {Object} emp
 * @returns {boolean}
 */
export function isEmployeeExcludedFromBT(emp) {
  if (!emp) return false;
  const rt = String(emp.role_terrain || "").toLowerCase().trim();
  const te = String(emp.type_employe || "").toLowerCase().trim();
  return ROLES_EXCLUS_BT.includes(rt) || ROLES_EXCLUS_BT.includes(te);
}

/**
 * 🛡️ [v13.65.4 — 2026-02-28] DURCISSEMENT DÉFENSIF de la lecture des
 * `employes_assignes[*]` d'un BT.
 *
 * Convention canonique (source de vérité) :
 *   { employee_id: string, employee_nom?: string, role?: string,
 *     dates_assignees?: string[] | null }
 *
 * Variantes legacy connues (imports, seeds tiers, agents automatiques) :
 *   - `employe_id`     → aliaser vers `employee_id`
 *   - `employe_nom`    → aliaser vers `employee_nom`
 *   - `dates`          → aliaser vers `dates_assignees`
 *   - `date_assignees` → aliaser vers `dates_assignees` (typo)
 *
 * ⚠️ RÈGLE STRICTE : la valeur CANONIQUE existante gagne toujours si présente.
 * Le legacy ne sert que de fallback quand le canonique est absent/undefined.
 *
 * Audit DB réelle 2026-02-28 : 0 BT en état non canonique. Ce module est
 * une défense en profondeur contre les imports ou seeds futurs qui
 * introduiraient ces variantes. Cf. RCA_V13_65_PRESENCE_PAR_JOUR.md § 11.
 *
 * @param {Object} entry - Une entrée brute de `bon.employes_assignes`.
 * @returns {Object} Une entrée avec les clés canoniques garanties (les clés
 *   originales sont préservées pour rétrocompat lecture ailleurs).
 */
export function normalizeEmployeAssigneEntry(entry) {
  if (!entry || typeof entry !== "object") return entry;
  const employee_id = entry.employee_id ?? entry.employe_id;
  const employee_nom = entry.employee_nom ?? entry.employe_nom;
  // Ordre : canonique > typo > legacy court
  const dates_assignees = Array.isArray(entry.dates_assignees)
    ? entry.dates_assignees
    : Array.isArray(entry.date_assignees)
      ? entry.date_assignees
      : Array.isArray(entry.dates)
        ? entry.dates
        : entry.dates_assignees; // preserve null/undefined explicite
  return {
    ...entry,
    ...(employee_id !== undefined && { employee_id }),
    ...(employee_nom !== undefined && { employee_nom }),
    ...(dates_assignees !== undefined && { dates_assignees }),
  };
}

/**
 * 🛡️ [v13.65.4 — 2026-02-28] Normalise TOUT le tableau `employes_assignes`
 * d'un BT. À appeler au mount du dialog d'édition + partout où on lit ce
 * champ à des fins de fusion/matching.
 */
export function normalizeEmployesAssignes(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeEmployeAssigneEntry);
}
